import { Segment } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React, { type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { MODES_REGLEMENTS } from '@/constants';
import { useBordereauRemise } from '@/hooks';
import { printBordereau } from '@/routes';
import type { ModeReglementsPointageType } from '@/types';
import {
  BordereauBonCAFListeTableHeader,
  BordereauBonCAFListeTableRow,
} from './BordereauBonCAFListe';
import {
  BordereauBonCCASListeTableHeader,
  BordereauBonCCASListeTableRow,
} from './BordereauBonCCASListe';
import {
  BordereauCarteBancaireInternetListeTableHeader,
  BordereauCarteBancaireInternetListeTableRow,
} from './BordereauCarteBancaireInternetListe';
import {
  BordereauCarteBancaireListeTableHeader,
  BordereauCarteBancaireListeTableRow,
} from './BordereauCarteBancaireListe';
import { BordereauCESUListeTableHeader, BordereauCESUListeTableRow } from './BordereauCESUListe';
import {
  BordereauChequeListeTableHeader,
  BordereauChequeListeTableRow,
} from './BordereauChequeListe';
import {
  BordereauChequeVacancesListeTableHeader,
  BordereauChequeVacancesListeTableRow,
} from './BordereauChequeVacancesListe';
import {
  BordereauNumeraireListeTableHeader,
  BordereauNumeraireListeTableRow,
} from './BordereauNumeraireListe';
import {
  BordereauPrelevementInternetListeTableHeader,
  BordereauPrelevementInternetListeTableRow,
} from './BordereauPrelevementInternetListe';
import {
  BordereauPrelevementListeTableHeader,
  BordereauPrelevementListeTableRow,
} from './BordereauPrelevementListe';
import {
  BordereauTicketListeTableHeader,
  BordereauTicketListeTableRow,
} from './BordereauTicketListe';
import {
  BordereauVirementListeTableHeader,
  BordereauVirementListeTableRow,
} from './BordereauVirementListe';

type BordereauListeProps = {
  type?: ModeReglementsPointageType;
};

const BordereauListe = (props: BordereauListeProps) => {
  const { type = MODES_REGLEMENTS.CHEQUE } = props;

  const { total, loading, bordereaux, setBordereaux, setSorts, setPage } = useBordereauRemise(type);

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort) ? null : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
      setBordereaux([]);
    },
    [setBordereaux, setSorts],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handlePrintDocument = React.useCallback((identifiant: number) => {
    request
      .get(printBordereau(identifiant), {
        headers: { Accept: 'application/json, application/pdf' },
      })
      .then((response: any) => {
        // Si la réponse contient une URL temporaire (JSON)
        if (response.url) {
          window.open(response.url, '_blank');
        } else {
          // Sinon, traiter comme un blob PDF (réponse directe)
          const blob = response.data || response;
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          const url = URL.createObjectURL(pdfBlob);

          window.open(url, '_blank');

          // Nettoyer après un délai
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 2000);
        }
      })
      .catch(() => {
        toast("Une erreur est survenue lors de l'ouverture du bordereau", { type: 'error' });
      });
  }, []);

  const Row = React.useMemo(() => {
    switch (type) {
      case MODES_REGLEMENTS.CHEQUE:
        return BordereauChequeListeTableRow;
      case MODES_REGLEMENTS.NUMERAIRE:
        return BordereauNumeraireListeTableRow;
      case MODES_REGLEMENTS.CESU:
        return BordereauCESUListeTableRow;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return BordereauChequeVacancesListeTableRow;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return BordereauCarteBancaireListeTableRow;
      case MODES_REGLEMENTS.VIREMENT:
        return BordereauVirementListeTableRow;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return BordereauPrelevementListeTableRow;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return BordereauCarteBancaireInternetListeTableRow;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return BordereauPrelevementInternetListeTableRow;
      case MODES_REGLEMENTS.TICKET:
        return BordereauTicketListeTableRow;
      case MODES_REGLEMENTS.BON_CCAS:
        return BordereauBonCCASListeTableRow;
      case MODES_REGLEMENTS.BON_CAF:
        return BordereauBonCAFListeTableRow;
      default:
        return null;
    }
  }, [type]);

  const Header = React.useMemo(() => {
    switch (type) {
      case MODES_REGLEMENTS.CHEQUE:
        return BordereauChequeListeTableHeader;
      case MODES_REGLEMENTS.NUMERAIRE:
        return BordereauNumeraireListeTableHeader;
      case MODES_REGLEMENTS.CESU:
        return BordereauCESUListeTableHeader;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return BordereauChequeVacancesListeTableHeader;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return BordereauCarteBancaireListeTableHeader;
      case MODES_REGLEMENTS.VIREMENT:
        return BordereauVirementListeTableHeader;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return BordereauPrelevementListeTableHeader;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return BordereauCarteBancaireInternetListeTableHeader;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return BordereauPrelevementInternetListeTableHeader;
      case MODES_REGLEMENTS.TICKET:
        return BordereauTicketListeTableHeader;
      case MODES_REGLEMENTS.BON_CCAS:
        return BordereauBonCCASListeTableHeader;
      case MODES_REGLEMENTS.BON_CAF:
        return BordereauBonCAFListeTableHeader;
      default:
        return null;
    }
  }, [type]);

  const renderRow = (item, __, rowProps) => (
    <Row item={item} rowProps={rowProps} onPrintDocument={handlePrintDocument} />
  );

  return (
    <Segment
      basic
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
        alignItems: 'flex-end',
        marginBottom: 0,
      }}
    >
      <div style={{ height: 450, width: '100%' }}>
        <ListeTable
          key={type}
          count={_.isEmpty(bordereaux) ? 0 : total}
          loading={loading}
          items={bordereaux}
          onSort={handleSort}
          perPage={50}
          onLoad={handleLoad}
          noResultsIcon=""
          noResultsMessage="Aucun bordereau de remise pour ce mode de règlement"
        >
          <ListeTable.Header>
            <Header />
          </ListeTable.Header>
          {renderRow as unknown as ReactNode}
        </ListeTable>
      </div>
    </Segment>
  );
};

export default BordereauListe;
